@layer refine.base {
  /* ========================================
       Base Styles & Defaults
       ======================================== */

  /* No unconditional `width: 100%` here, on purpose — matches every other field's own `:host`
     (`ore-input`, `ore-select`, `ore-number-input`, `ore-combobox`, `ore-date-picker`): shrink to
     `min-width` by default, opt into full width via `:host([fullwidth])` below. A hardcoded
     `width: 100%` here (the previous default) meant the slider always claimed the entire row in
     a flex layout regardless of layout intent — e.g. sitting next to a label/hint column in a
     settings-style row, it forced that column down to near its text's min-content width instead
     of sharing the row the way a same-row `ore-select`/`ore-number-input` naturally does. */
  :host {
    --_size: var(--slider-size, var(--size-5));
    --_height: var(--slider-height, var(--size-1));
    --_track: var(--slider-track-bg, var(--color-contrast-300));
    --_fill: var(--slider-fill, var(--color-neutral));
    --_thumb: var(--slider-thumb-bg, var(--color-contrast-50));
    --_thumb-size: var(--_size);
    --_thumb-inset: var(--slider-thumb-inset, var(--size-2-5));
    --_touch-target: var(--size-11);
    --_font-size: var(--text-sm);
    --_shadow: var(--color-neutral-focus-shadow);

    position: relative;
    display: inline-flex;
    flex-wrap: wrap;
    gap: var(--_gap, var(--size-3));
    align-items: center;
    min-width: var(--slider-min-width, 12rem);
    touch-action: none;
    cursor: pointer;
    user-select: none;
  }

  /* Explicit opt-in still forces full width outside a stretching grid/flex context — same
     convention as ore-input's own :host([fullwidth]). */
  :host([fullwidth]) {
    width: 100%;
  }
}

/* ========================================
     Track, Fill & Thumb
     ======================================== */

.slider-container {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex: 1;
  align-items: center;
  width: 100%;
  min-height: var(--_touch-target);
  padding-inline: var(--_thumb-inset);
}

.slider-track {
  position: relative;
  width: 100%;
  height: var(--_height);
  background: var(--_track);
  border-radius: var(--rounded-full);
}

/* Fill: always driven by --_fill-start / --_fill-width */
.slider-fill {
  position: absolute;
  top: 0;
  left: var(--_fill-start, 0%);
  width: var(--_fill-width, 0%);
  height: 100%;
  background: var(--_fill);
  border-radius: var(--rounded-full);
  transition: var(--_motion-transition, left var(--transition-fast), width var(--transition-fast));
}

:host([data-dragging]) .slider-fill {
  transition: none;
}

.slider-thumb {
  position: absolute;
  top: 50%;
  z-index: 1;
  box-sizing: border-box;
  width: var(--_thumb-size);
  height: var(--_thumb-size);
  cursor: grab;
  background: var(--_thumb);
  border: 2px solid var(--_fill);
  border-radius: var(--rounded-full);
  box-shadow: var(--shadow-sm);
  transform: translate(-50%, -50%);
  transition: var(--_motion-transition, box-shadow var(--transition-fast), left var(--transition-fast));
  will-change: transform;
}

:host([data-dragging]) .slider-thumb {
  cursor: grabbing;
  box-shadow: var(--_shadow);
  transition: var(--_motion-transition, box-shadow var(--transition-fast));
}

/* ── Single-value thumb ─────────────────────────────────── */

.slider-thumb-sole {
  left: var(--_thumb-pos, 0%);
}

:host(:focus-visible) .slider-thumb-sole,
:host(:active) .slider-thumb-sole {
  box-shadow: var(--_shadow);
}

/* ── Range thumbs ───────────────────────────────────────── */

.slider-thumb-start {
  left: var(--_thumb-start, 0%);
}

.slider-thumb-end {
  left: var(--_thumb-end, 100%);
}

.slider-thumb-start:focus-visible,
.slider-thumb-end:focus-visible {
  z-index: 2;
  outline: var(--border-2) solid var(--_fill);
  outline-offset: var(--size-0-5);
  box-shadow: var(--_shadow);
}

/* ── Visibility gating ──────────────────────────────────── */

:host(:not([range])) .slider-thumb-start,
:host(:not([range])) .slider-thumb-end {
  display: none;
}

:host([range]) .slider-thumb-sole {
  display: none;
}

@media (hover: hover) {
  :host(:not([disabled])) .slider-thumb:hover {
    box-shadow: var(--_shadow);
  }
}

@media (forced-colors: active) {
  :host(:focus-visible) .slider-thumb-sole,
  .slider-thumb-start:focus-visible,
  .slider-thumb-end:focus-visible {
    outline: 2px solid Highlight;
    outline-offset: 2px;
    box-shadow: none;
  }
}

@layer refine.overrides {
  :host {
    --_fill: var(--slider-fill, var(--_theme-base));
    --_shadow: var(--_theme-shadow);
  }
}

/* ========================================
     Label
     ======================================== */

.label {
  font-size: var(--_font-size);
  color: var(--color-contrast);
  white-space: nowrap;
}

.helper-text[hidden] {
  display: none;
}

.helper-text {
  width: 100%;
  font-size: var(--text-xs);
  line-height: var(--leading-tight);
  color: color-mix(in oklch, var(--color-contrast-500) 80%, var(--color-canvas));
}

.helper-text[role='alert'] {
  color: var(--color-error);
}
